import type { ChildSupportLevel } from "@/calcs/types";
import type {
    CaseData,
    HighIncomeEstimatorModel,
    HighIncomePrimaryEstimateModel,
    HighIncomeSharedEstimateModel,
    HighIncomeSharedPartyPair,
} from "@/types/case";
import type { Worksheet1Model } from "@/calcs/documents/worksheet1";
import { roundTo } from "@/calcs/utils";

const CHART_STEP = 1000;

/** Joint physical care: each parent's basic (E) × 1.5 (F), then ÷ 2 (G). Matches Worksheet 1 Section 4. */
export const SHARED_JOINT_PHYSICAL_CARE_MULTIPLIER = 1.5;
export const SHARED_JOINT_PHYSICAL_CARE_DIVISOR = 2;

export type ExtrapolationMethodId =
    | "percentageAtCap"
    | "linearExtension"
    | "squareRoot"
    | "logarithmic"
    | "capPlusHalfRate"
    | "capPlus75Rate"
    | "pctIncomeFloor"
    | "marginalRateCap"
    | "asymptotic"
    | "elasticity"
    | "inflationStyle";

function getAmountAt(levels: ChildSupportLevel[], income: number, childIndex: number): number {
    if (!levels.length || childIndex < 0 || childIndex > 4) return 0;
    const cap = levels.reduce((max, level) => Math.max(max, level.combinedAdjustedNetIncomeMaximum ?? 0), 0);
    const clamped = Math.min(Math.max(income, 0), cap);
    const level = levels.find(
        (x) => x.combinedAdjustedNetIncomeMinimum <= clamped && x.combinedAdjustedNetIncomeMaximum >= clamped
    ) ?? levels[levels.length - 1];
    return level.amounts[childIndex] ?? 0;
}

function extrapolate(
    method: ExtrapolationMethodId,
    income: number,
    amountAtCap: number,
    amountJustBelowCap: number,
    cap: number
): number {
    if (income <= cap) return amountAtCap;
    const excess = income - cap;
    const pctAtCap = amountAtCap / cap;
    switch (method) {
        case "percentageAtCap":
            return (amountAtCap / cap) * income;
        case "linearExtension": {
            const slope = amountJustBelowCap > 0 ? (amountAtCap - amountJustBelowCap) / (cap - (cap - CHART_STEP)) : pctAtCap;
            return amountAtCap + excess * slope;
        }
        case "squareRoot":
            return amountAtCap * Math.sqrt(income / cap);
        case "logarithmic":
            return amountAtCap * (1 + Math.log(income / cap));
        case "capPlusHalfRate":
            return amountAtCap + excess * pctAtCap * 0.5;
        case "capPlus75Rate":
            return amountAtCap + excess * pctAtCap * 0.75;
        case "pctIncomeFloor":
            return Math.max(amountAtCap, income * pctAtCap * 0.8);
        case "marginalRateCap": {
            const marginalCap = 0.15;
            return amountAtCap + excess * Math.min(pctAtCap, marginalCap);
        }
        case "asymptotic": {
            const ceilingMultiplier = 2;
            return amountAtCap * (1 + ceilingMultiplier * (1 - cap / income));
        }
        case "elasticity": {
            const slope = amountJustBelowCap > 0 ? (amountAtCap - amountJustBelowCap) / CHART_STEP : pctAtCap;
            const elasticity = (slope * cap) / amountAtCap;
            const eps = Number.isFinite(elasticity) && elasticity > 0 ? elasticity : 1;
            return amountAtCap * Math.pow(income / cap, eps);
        }
        case "inflationStyle": {
            const growthPerCap = 0.02;
            return amountAtCap * Math.pow(1 + growthPerCap, (income - cap) / cap);
        }
        default:
            return (amountAtCap / cap) * income;
    }
}

const DEFAULT_EXTRAPOLATION_METHOD: ExtrapolationMethodId = "percentageAtCap";

/**
 * Line K at a hypothetical combined basic support D, holding income shares (B) and health line I fixed.
 * Matches joint physical care E–K logic in this module (J = G + I). Used for shared high-income chart series.
 */
export function computeSharedJointLineKsFromCombinedBasic(
    dHigh: number,
    pctP: number,
    pctR: number,
    petitionerLineI: number,
    respondentLineI: number
): { petitionerLineK: number; respondentLineK: number } {
    const petitionerLineE = roundTo((dHigh * pctP) / 100.0, 2);
    const respondentLineE = roundTo((dHigh * pctR) / 100.0, 2);

    const petitionerLineF = roundTo(petitionerLineE * SHARED_JOINT_PHYSICAL_CARE_MULTIPLIER, 2);
    const respondentLineF = roundTo(respondentLineE * SHARED_JOINT_PHYSICAL_CARE_MULTIPLIER, 2);

    const petitionerLineG = roundTo(petitionerLineF / SHARED_JOINT_PHYSICAL_CARE_DIVISOR, 2);
    const respondentLineG = roundTo(respondentLineF / SHARED_JOINT_PHYSICAL_CARE_DIVISOR, 2);

    const pI = roundTo(petitionerLineI, 2);
    const rI = roundTo(respondentLineI, 2);

    const petitionerLineJ = roundTo(petitionerLineG + pI, 2);
    const respondentLineJ = roundTo(respondentLineG + rI, 2);

    let petitionerLineK = 0;
    let respondentLineK = 0;
    if (petitionerLineJ > respondentLineJ) {
        petitionerLineK = roundTo(petitionerLineJ - respondentLineJ, 2);
    } else if (respondentLineJ > petitionerLineJ) {
        respondentLineK = roundTo(respondentLineJ - petitionerLineJ, 2);
    }

    return { petitionerLineK, respondentLineK };
}

const VALID_METHOD_IDS: ExtrapolationMethodId[] = [
    "percentageAtCap", "linearExtension", "squareRoot", "logarithmic",
    "capPlusHalfRate", "capPlus75Rate", "pctIncomeFloor", "marginalRateCap",
    "asymptotic", "elasticity", "inflationStyle",
];

/**
 * Section 3: NCP line G from income area, low-income line D, and proportionate share F (same rules as worksheet1Calcs).
 */
function computeSection3NcpLineG(
    isNcp: boolean,
    isLowIncome: boolean,
    incomeArea: string | undefined,
    lineD: number,
    lineF: number
): number {
    if (!isNcp) return 0;
    switch (incomeArea) {
        case "a":
            return roundTo(lineD, 2);
        case "b":
            if (isLowIncome) {
                return roundTo(Math.min(lineD, lineF), 2);
            }
            return roundTo(lineF, 2);
        case "c":
            return roundTo(lineF, 2);
        default:
            return roundTo(lineF, 2);
    }
}

/**
 * Worksheet 1 Section 3 row snapshot using extrapolated combined basic (E) instead of the guideline table.
 */
export function computePrimaryPhysicalEstimate(
    worksheet1: Worksheet1Model,
    dHigh: number
): HighIncomePrimaryEstimateModel | null {
    const s3 = worksheet1.section3;
    if (!s3?.petitioner || !s3?.respondent || s3.combined == null) {
        return null;
    }

    const p = s3.petitioner;
    const r = s3.respondent;

    const combinedLineA = roundTo(s3.combined.lineA ?? 0, 2);
    const combinedLineC = s3.combined.lineC ?? 0;

    const lineA: HighIncomeSharedPartyPair = {
        petitioner: roundTo(p.lineA ?? 0, 2),
        respondent: roundTo(r.lineA ?? 0, 2),
    };
    const lineB: HighIncomeSharedPartyPair = {
        petitioner: roundTo(p.lineB ?? 0, 2),
        respondent: roundTo(r.lineB ?? 0, 2),
    };

    const petitionerLineF = roundTo((dHigh * (p.lineB ?? 0)) / 100.0, 2);
    const respondentLineF = roundTo((dHigh * (r.lineB ?? 0)) / 100.0, 2);
    const lineF: HighIncomeSharedPartyPair = {
        petitioner: petitionerLineF,
        respondent: respondentLineF,
    };

    const petitionerLineG = computeSection3NcpLineG(
        !!p.isNoncustodialParent,
        !!p.isLowIncome,
        p.debug?.incomeArea,
        p.lineD ?? 0,
        petitionerLineF
    );
    const respondentLineG = computeSection3NcpLineG(
        !!r.isNoncustodialParent,
        !!r.isLowIncome,
        r.debug?.incomeArea,
        r.lineD ?? 0,
        respondentLineF
    );
    const lineG: HighIncomeSharedPartyPair = {
        petitioner: petitionerLineG,
        respondent: respondentLineG,
    };

    const lineH: HighIncomeSharedPartyPair = {
        petitioner: roundTo(p.lineH ?? 0, 2),
        respondent: roundTo(r.lineH ?? 0, 2),
    };
    const lineI: HighIncomeSharedPartyPair = {
        petitioner: roundTo(p.lineI ?? 0, 2),
        respondent: roundTo(r.lineI ?? 0, 2),
    };

    const petitionerLineJ1 = roundTo(petitionerLineG + (p.lineI ?? 0), 2);
    const respondentLineJ1 = roundTo(respondentLineG + (r.lineI ?? 0), 2);
    const lineJ1: HighIncomeSharedPartyPair = {
        petitioner: petitionerLineJ1,
        respondent: respondentLineJ1,
    };

    return {
        combinedLineA,
        combinedLineC,
        combinedBasicE: roundTo(dHigh, 2),
        lineA,
        lineB,
        lineF,
        lineG,
        lineH,
        lineI,
        lineJ1,
    };
}

function normalizeMethod(raw: string | null | undefined): ExtrapolationMethodId {
    if (raw == null || typeof raw !== "string") return DEFAULT_EXTRAPOLATION_METHOD;
    const trimmed = String(raw).trim();
    return (VALID_METHOD_IDS as string[]).includes(trimmed) ? (trimmed as ExtrapolationMethodId) : DEFAULT_EXTRAPOLATION_METHOD;
}

/**
 * Computes high-income estimator values when combined income is at or above the guideline cap.
 * Uses percentageAtCap extrapolation when no method is specified.
 * Returns null when combined income is below cap (caller should not set documents.highIncomeEstimator).
 */
export function calcHighIncomeEstimator(
    caseData: CaseData,
    worksheet1: Worksheet1Model,
    supportLevels: ChildSupportLevel[],
    capIncome: number
): HighIncomeEstimatorModel | null {
    const custodyArrangement = caseData.children?.custodyArrangement ?? "partyA";
    const combinedIncome = roundTo(
        custodyArrangement === "shared"
            ? (worksheet1.section4?.combined?.lineA ?? 0)
            : (worksheet1.section3?.combined?.lineA ?? 0),
        2
    );

    if (combinedIncome <= 0 || combinedIncome < capIncome) {
        return null;
    }

    const childCount = Math.min(5, Math.max(1, caseData.children?.count ?? 1));
    const childIndex = childCount - 1;
    const amountAtCap = getAmountAt(supportLevels, capIncome, childIndex);
    const amountJustBelowCap = getAmountAt(supportLevels, capIncome - CHART_STEP, childIndex);
    const method = normalizeMethod(caseData.documents?.highIncomeEstimator?.extrapolationMethod);

    const resultAmount = combinedIncome <= capIncome
        ? getAmountAt(supportLevels, combinedIncome, childIndex)
        : extrapolate(method, combinedIncome, amountAtCap, amountJustBelowCap, capIncome);

    const dHigh = resultAmount;
    const extrapolatedPercent = combinedIncome > 0 ? roundTo((dHigh / combinedIncome) * 100.0, 2) : 0;

    if (custodyArrangement === "shared") {
        const s4 = worksheet1.section4;
        if (!s4?.petitioner || !s4?.respondent) {
            return null;
        }

        const pctP = roundTo(s4.petitioner.lineB ?? 0, 2);
        const pctR = roundTo(s4.respondent.lineB ?? 0, 2);

        const petitionerLineE = roundTo((dHigh * pctP) / 100.0, 2);
        const respondentLineE = roundTo((dHigh * pctR) / 100.0, 2);

        const petitionerLineF = roundTo(petitionerLineE * SHARED_JOINT_PHYSICAL_CARE_MULTIPLIER, 2);
        const respondentLineF = roundTo(respondentLineE * SHARED_JOINT_PHYSICAL_CARE_MULTIPLIER, 2);

        const petitionerLineG = roundTo(petitionerLineF / SHARED_JOINT_PHYSICAL_CARE_DIVISOR, 2);
        const respondentLineG = roundTo(respondentLineF / SHARED_JOINT_PHYSICAL_CARE_DIVISOR, 2);

        const petitionerLineH = roundTo(s4.petitioner.lineH ?? 0, 2);
        const respondentLineH = roundTo(s4.respondent.lineH ?? 0, 2);
        const petitionerLineI = roundTo(s4.petitioner.lineI ?? 0, 2);
        const respondentLineI = roundTo(s4.respondent.lineI ?? 0, 2);

        const petitionerLineJ = roundTo(petitionerLineG + petitionerLineI, 2);
        const respondentLineJ = roundTo(respondentLineG + respondentLineI, 2);

        const { petitionerLineK, respondentLineK } = computeSharedJointLineKsFromCombinedBasic(
            dHigh,
            pctP,
            pctR,
            s4.petitioner.lineI ?? 0,
            s4.respondent.lineI ?? 0
        );

        let netOffsetPayor: "petitioner" | "respondent" | null = null;
        let estimatedMonthlyObligation = 0;
        if (petitionerLineK > 0) {
            netOffsetPayor = "petitioner";
            estimatedMonthlyObligation = petitionerLineK;
        } else if (respondentLineK > 0) {
            netOffsetPayor = "respondent";
            estimatedMonthlyObligation = respondentLineK;
        }

        const sharedEstimate: HighIncomeSharedEstimateModel = {
            petitionerLineB: pctP,
            respondentLineB: pctR,
            combinedBasicD: dHigh,
            lineE: { petitioner: petitionerLineE, respondent: respondentLineE },
            lineF: { petitioner: petitionerLineF, respondent: respondentLineF },
            lineG: { petitioner: petitionerLineG, respondent: respondentLineG },
            lineH: { petitioner: petitionerLineH, respondent: respondentLineH },
            lineI: { petitioner: petitionerLineI, respondent: respondentLineI },
            lineJ: { petitioner: petitionerLineJ, respondent: respondentLineJ },
            lineK: { petitioner: petitionerLineK, respondent: respondentLineK },
        };

        return {
            extrapolationMethod: method,
            extrapolatedPercent,
            estimatedMonthlyObligation,
            combinedIncome,
            netOffsetPayor,
            sharedEstimate,
        };
    }

    let payorPercentageOfCombined: number | null = null;
    if (worksheet1.section3) {
        const ncp = worksheet1.section3.petitioner?.isNoncustodialParent
            ? worksheet1.section3.petitioner
            : worksheet1.section3.respondent;
        payorPercentageOfCombined = ncp?.lineB != null ? roundTo(ncp.lineB, 2) : null;
    }

    const displayAmount =
        payorPercentageOfCombined != null && Number.isFinite(payorPercentageOfCombined)
            ? roundTo(combinedIncome * (extrapolatedPercent / 100.0) * (payorPercentageOfCombined / 100.0), 2)
            : roundTo(combinedIncome * (extrapolatedPercent / 100.0), 2);

    const primaryPhysicalEstimate = computePrimaryPhysicalEstimate(worksheet1, dHigh);

    return {
        extrapolationMethod: method,
        extrapolatedPercent,
        estimatedMonthlyObligation: displayAmount,
        combinedIncome,
        payorPercentageOfCombined: payorPercentageOfCombined ?? undefined,
        primaryPhysicalEstimate: primaryPhysicalEstimate ?? undefined,
    };
}
